import React from 'react';

type SkillTopic = 'rendering' | 'gpu' | 'ml';

interface SkillItem {
  name: string;
  tags: string[];
  topic?: SkillTopic;
}

interface SkillCategory {
  id: string;
  label: string;
  items: SkillItem[];
}

const categories: SkillCategory[] = [
  {
    id: 'skills-graphics-apis',
    label: 'Rendering APIs & Engine Internals',
    items: [
      { name: 'Vulkan', topic: 'gpu', tags: ['rasterization', 'ray tracing pipeline', 'sync2', 'bindless resources'] },
      { name: 'Unreal Engine 5', topic: 'rendering', tags: ['RHI', 'RDG', 'custom render passes', 'rendering path investigation'] },
      { name: 'DirectX', topic: 'gpu', tags: ['D3D11 / D3D12', 'graphics API familiarity'] },
      { name: 'OpenGL', topic: 'rendering', tags: ['PBR pipeline', 'deferred rendering', 'engine architecture'] },
    ],
  },
  {
    id: 'skills-rendering-techniques',
    label: 'Rendering Architecture & Techniques',
    items: [
      { name: 'Path Tracing & Ray Tracing', topic: 'rendering', tags: ['BVH traversal', 'Disney BSDF', 'global illumination'] },
      { name: 'GPU-Driven Rendering', topic: 'rendering', tags: ['cluster rendering', 'indirect draws', 'occlusion culling'] },
      { name: 'Global Illumination', topic: 'rendering', tags: ['Radiance Cascades', '2D GI', 'light propagation'] },
      { name: 'Gaussian Splatting', topic: 'rendering', tags: ['3D reconstruction', 'volumetric rendering'] },
      { name: 'Synchronization & Resource Flow', topic: 'gpu', tags: ['barriers', 'resource lifetime', 'pass dependency management'] },
      { name: 'GPU Skinning', topic: 'gpu', tags: ['mesh shader culling', 'XR avatars'] },
    ],
  },
  {
    id: 'skills-gpu-ml',
    label: 'Compute & Profiling',
    items: [
      { name: 'CUDA', topic: 'gpu', tags: ['shared memory tiling', 'GEMM kernels', 'cuBLAS comparison', 'CUDA Events'] },
      { name: 'OpenCL', topic: 'gpu', tags: ['compute kernels', 'workgroup sweeps', 'occupancy profiling'] },
      { name: 'RenderDoc / Nsight Graphics', topic: 'gpu', tags: ['GPU capture', 'frame analysis', 'profiling workflow'] },
    ],
  },
  {
    id: 'skills-core-languages',
    label: 'Languages & Shader Tooling',
    items: [
      { name: 'C++', tags: ['systems programming', 'engine work', 'C++20'] },
      { name: 'C#', tags: ['engine tooling', 'runtime systems'] },
      { name: 'Python', tags: ['benchmark tooling', 'research scripts'] },
      { name: 'GLSL', topic: 'rendering', tags: ['vertex', 'fragment', 'compute'] },
      { name: 'HLSL', topic: 'rendering', tags: ['shader authoring', 'real-time rendering'] },
      { name: 'Slang', topic: 'rendering', tags: ['shader abstractions', 'cross-compilation'] },
    ],
  },
  {
    id: 'skills-ml-techniques',
    label: 'ML Systems Research',
    items: [
      { name: 'PyTorch / Training Tooling', topic: 'ml', tags: ['training pipeline', 'experimentation tooling'] },
      { name: 'Transformer Internals', topic: 'ml', tags: ['implemented from scratch', 'multi-head self-attention', 'BPE tokenizer'] },
      { name: 'Backpropagation', topic: 'ml', tags: ['manual derivation', 'gradient validation'] },
      { name: 'Latent Space Models', topic: 'ml', tags: ['latent representations', 'model exploration'] },
    ],
  },
];

const tools = ['SPIRV-Cross', 'Git', 'Visual Studio', 'CLion', 'Blender'];

const TechnicalSkills: React.FC = () => (
  <section id="skills" data-topic="gpu">
    <div className="content">

      <h2 className="section-heading">Skills</h2>

      {categories.map(cat => (
        <div
          key={cat.label}
          id={cat.id}
          className="topic-block"
          style={{ marginBottom: '2rem' }}
        >
          <div className="topic-label" style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            {cat.label}
          </div>
          {cat.items.map(item => (
            <div key={item.name} className="skill-row">
              <span className="skill-name" data-topic={item.topic}>
                {item.topic && <span className="topic-dot skill-topic-dot" data-topic={item.topic} aria-hidden="true" />}
                {item.name}
              </span>
              <div className="skill-tags">
                {item.tags.map(t => (
                  <span key={t} className="badge">{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}

      <div id="skills-tools" className="topic-label" data-topic="gpu" style={{ fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
        Supporting Tooling
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
        {tools.map(t => <span key={t} className="badge">{t}</span>)}
      </div>

    </div>
  </section>
);

export default TechnicalSkills;
